import React, { useState, useMemo } from 'react';
import { FixedSizeList as List } from 'react-window';
import { formatDateTime, formatDuration, formatSyncType } from '../../utils/formatUtils';
import { deleteSyncRecord, deleteAllSyncHistory } from '../../api/syncApi';
import { toast } from 'react-toastify';

// ── Helpers ──────────────────────────────────────────────────────────────────

const getItemCount = (results) => {
  if (!results) return 'N/A';
  if (results.courses !== undefined)
    return typeof results.courses === 'object' ? `${results.courses.total || 0} courses` : `${results.courses} courses`;
  if (results.chapters !== undefined)
    return typeof results.chapters === 'object' ? `${results.chapters.total || 0} chapters` : `${results.chapters} chapters`;
  if (results.contents !== undefined)
    return typeof results.contents === 'object' ? `${results.contents.total || 0} contents` : `${results.contents} contents`;
  if (results.mentors !== undefined)
    return typeof results.mentors === 'object' ? `${results.mentors.total || 0} mentors` : `${results.mentors} mentors`;
  if (results.attempts !== undefined)
    return typeof results.attempts === 'object' ? `${results.attempts.total || 0} attempts` : `${results.attempts} attempts`;
  if (results.batches !== undefined)
    return typeof results.batches === 'object' ? `${results.batches.total || 0} batches` : `${results.batches} batches`;
  if (results.total !== undefined && (results.successful !== undefined || results.created !== undefined))
    return `${results.total} users`;
  if (results.totalCourses !== undefined)
    return `${(results.totalCreated || 0) + (results.totalUpdated || 0)} chapters`;
  if (results.totalContents !== undefined) return `${results.totalContents} contents`;
  if (results.totalProcessed !== undefined) return `${results.totalProcessed} exams`;
  if (results.attemptsProcessed !== undefined) return `${results.attemptsProcessed} attempts`;
  if (results.stepsCompleted !== undefined || results.stepsTotal !== undefined)
    return `${results.stepsCompleted || 0}/${results.stepsTotal || 0} steps`;
  if (results.stats !== undefined && results.date !== undefined)
    return `${results.stats?.total || results.stats?.contentAttemptsProcessed || 0} attempts`;
  if (results.totalAttempts !== undefined) return `${results.totalAttempts} attempts`;
  if (results.totalChapters !== undefined) return `${results.totalChapters} chapters`;
  return 'N/A';
};

const getTypeIcon = (type) => {
  if (!type) return 'bi-question-circle';
  if (type.includes('course')) return 'bi-book';
  if (type.includes('chapter')) return 'bi-list-nested';
  if (type.includes('content')) return 'bi-file-earmark-text';
  if (type.includes('mentor')) return 'bi-person';
  if (type.includes('attempts')) return 'bi-clipboard-check';
  if (type.includes('batch')) return 'bi-people';
  if (type.includes('users')) return 'bi-people-fill';
  return 'bi-arrow-repeat';
};

const StatusBadge = ({ status }) => {
  const map = {
    completed: { cls: 'status-completed', icon: 'bi-check-circle' },
    running:   { cls: 'status-running',   icon: 'bi-arrow-repeat'  },
    failed:    { cls: 'status-failed',    icon: 'bi-x-circle'      },
  };
  const { cls = 'status-unknown', icon = 'bi-question-circle' } = map[status] || {};
  return (
    <span className={`history-cell-status-badge ${cls}`}>
      <i className={`bi ${icon}`}></i>
      {status || 'unknown'}
    </span>
  );
};

// ── Grid column template helpers ──────────────────────────────────────────────

const getGridTemplate = (isDeleteMode) =>
  isDeleteMode
    ? '36px 80px 1fr 110px 160px 110px 90px 130px'
    : '80px 1fr 110px 160px 110px 90px 110px';

// ── Row ───────────────────────────────────────────────────────────────────────

const Row = ({ index, style, data }) => {
  const {
    filteredHistory,
    isDeleteMode,
    selectedItems,
    toggleSelectedItem,
    onDeleteSingle,
    isDeletingItem,
    onViewDetails,
    loadingDetailsId,
  } = data;

  const sync = filteredHistory[index];
  if (!sync) return null;

  const isSelected = selectedItems.includes(sync.id);
  const isLoadingDetails = loadingDetailsId === sync.id;
  const isDeletingThis = isDeletingItem === sync.id;
  const isOdd = index % 2 === 1;

  return (
    <div
      style={style}
      className={[
        'history-grid-row',
        isOdd ? 'is-odd' : '',
        isSelected ? 'is-selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: getGridTemplate(isDeleteMode),
          alignItems: 'center',
          gap: '8px',
          height: '100%',
        }}
      >
        {isDeleteMode && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <input
              type="checkbox"
              className="history-checkbox"
              checked={isSelected}
              onChange={() => toggleSelectedItem(sync.id)}
            />
          </div>
        )}

        {/* ID */}
        <div className="history-cell-id" title={String(sync.id)}>
          #{sync.id}
        </div>

        {/* Type */}
        <div className="history-cell-type">
          <i className={`bi ${getTypeIcon(sync.type)} history-cell-type-icon`}></i>
          <span>{formatSyncType(sync.type)}</span>
        </div>

        {/* Status */}
        <div>
          <StatusBadge status={sync.status} />
        </div>

        {/* Start time */}
        <div className="history-cell-text">{formatDateTime(sync.startTime)}</div>

        {/* Duration */}
        <div className="history-cell-text">{formatDuration(sync)}</div>

        {/* Items */}
        <div className="history-cell-text">{getItemCount(sync.results)}</div>

        {/* Actions */}
        <div className="history-cell-actions">
          {isDeleteMode ? (
            <button
              className="history-action-btn btn-delete"
              onClick={() => onDeleteSingle(sync.id)}
              disabled={isDeletingThis}
            >
              {isDeletingThis ? (
                <span className="spinner-border spinner-border-sm" style={{ width: '12px', height: '12px' }}></span>
              ) : (
                <i className="bi bi-trash"></i>
              )}
              {isDeletingThis ? 'Deleting' : 'Delete'}
            </button>
          ) : (
            <button
              className="history-action-btn btn-details"
              onClick={() => onViewDetails?.(sync.id)}
              disabled={isLoadingDetails}
            >
              {isLoadingDetails ? (
                <span className="spinner-border spinner-border-sm" style={{ width: '12px', height: '12px' }}></span>
              ) : (
                <i className="bi bi-info-circle"></i>
              )}
              {isLoadingDetails ? 'Loading' : 'Details'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const SYNC_TYPES = [
  'all',
  'users-sync',
  'course-sync',
  'chapter-sync',
  'content-sync',
  'mentor-sync',
  'batch-sync',
  'attempt-answers-sync',
  'single-attempts-sync',
  'content-attempts-sync',
  'all-sync',
];

const STATUS_OPTIONS = ['all', 'completed', 'failed', 'running'];

const ROW_HEIGHT = 60;
const MAX_TABLE_HEIGHT = 420;

const HistoryTable = ({ history, onViewDetails, loadingDetailsId, onHistoryChanged }) => {
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isDeletingItem, setIsDeletingItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Filtered list
  const filteredHistory = useMemo(() => {
    if (!history) return [];
    return history.filter((item) => {
      if (filterType !== 'all' && item.type !== filterType) return false;
      if (filterStatus !== 'all' && item.status !== filterStatus) return false;
      return true;
    });
  }, [history, filterType, filterStatus]);

  const hasActiveFilters = filterType !== 'all' || filterStatus !== 'all';

  // ── Selection helpers ────────────────────────────────────────────────────────

  const toggleSelectedItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === filteredHistory.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredHistory.map((item) => item.id));
    }
  };

  // ── Delete handlers ──────────────────────────────────────────────────────────

  const handleToggleDeleteMode = () => {
    setIsDeleteMode((prev) => !prev);
    setSelectedItems([]);
  };

  const handleDeleteSingle = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sync record?')) return;
    setIsDeletingItem(id);
    try {
      await deleteSyncRecord(id);
      toast.success('Sync record deleted successfully');
      onHistoryChanged?.();
    } catch (err) {
      toast.error(`Failed to delete sync record: ${err.message}`);
    } finally {
      setIsDeletingItem(null);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) {
      toast.warning('No items selected for deletion');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedItems.length} selected sync record(s)?`)) return;

    setIsDeleting(true);
    try {
      for (const id of selectedItems) {
        setIsDeletingItem(id);
        await deleteSyncRecord(id);
      }
      toast.success(`Successfully deleted ${selectedItems.length} sync record(s)`);
      setSelectedItems([]);
      onHistoryChanged?.();
    } catch (err) {
      toast.error(`Failed to delete selected records: ${err.message}`);
    } finally {
      setIsDeleting(false);
      setIsDeletingItem(null);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL sync history? This action cannot be undone.')) return;
    setIsDeleting(true);
    try {
      const result = await deleteAllSyncHistory();
      toast.success(`Successfully deleted ${result.count} sync records`);
      onHistoryChanged?.();
    } catch (err) {
      toast.error(`Failed to delete all sync history: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  const listHeight = Math.min(MAX_TABLE_HEIGHT, filteredHistory.length * ROW_HEIGHT);

  const itemData = {
    filteredHistory,
    isDeleteMode,
    selectedItems,
    toggleSelectedItem,
    onDeleteSingle: handleDeleteSingle,
    isDeletingItem,
    onViewDetails,
    loadingDetailsId,
  };

  return (
    <div className="history-table-card theme-card">
      {/* Toolbar */}
      <div className="history-table-toolbar">
        <h2 className="history-table-title">
          <i className="bi bi-clock-history" style={{ color: 'var(--accent-blue)' }}></i>
          Sync History
        </h2>

        <div className="history-table-actions">
          <button
            className={`history-toolbar-btn ${isDeleteMode ? 'btn-delete-mode-on' : ''}`}
            onClick={handleToggleDeleteMode}
          >
            <i className={`bi ${isDeleteMode ? 'bi-x' : 'bi-trash'}`}></i>
            {isDeleteMode ? 'Cancel' : 'Delete Mode'}
          </button>

          {isDeleteMode && (
            <>
              <button
                className="history-toolbar-btn btn-delete-selected"
                onClick={handleDeleteSelected}
                disabled={selectedItems.length === 0 || isDeleting}
              >
                {isDeleting ? (
                  <span className="spinner-border spinner-border-sm" style={{ width: '12px', height: '12px' }}></span>
                ) : (
                  <i className="bi bi-trash"></i>
                )}
                Delete Selected ({selectedItems.length})
              </button>

              <button
                className="history-toolbar-btn btn-delete-all"
                onClick={handleDeleteAll}
                disabled={isDeleting}
              >
                <i className="bi bi-trash-fill"></i>
                Delete All
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="history-filters-row">
        <span className="history-filter-label">
          <i className="bi bi-funnel"></i>
          Filters
        </span>

        <select
          className="history-filter-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          {SYNC_TYPES.map((t) => (
            <option key={t} value={t}>
              {t === 'all' ? 'All Types' : formatSyncType(t)}
            </option>
          ))}
        </select>

        <select
          className="history-filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s === 'all' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>

        {hasActiveFilters && (
          <>
            <div className="history-filter-divider"></div>
            <button
              className="history-filter-clear-btn"
              onClick={() => {
                setFilterType('all');
                setFilterStatus('all');
              }}
            >
              <i className="bi bi-x"></i>
              Clear
            </button>
          </>
        )}

        <span className="history-filter-count">
          {filteredHistory.length} of {history?.length ?? 0} records
        </span>
      </div>

      {/* Empty state */}
      {(!history || history.length === 0) && (
        <div className="history-empty">
          <i className="bi bi-inbox history-empty-icon"></i>
          <span>No sync history available</span>
        </div>
      )}

      {history && history.length > 0 && filteredHistory.length === 0 && (
        <div className="history-empty">
          <i className="bi bi-search history-empty-icon"></i>
          <span>No records match the current filters</span>
        </div>
      )}

      {filteredHistory.length > 0 && (
        <>
          {/* Grid header */}
          <div
            className="history-grid-header"
            style={{
              display: 'grid',
              gridTemplateColumns: getGridTemplate(isDeleteMode),
              gap: '8px',
            }}
          >
            {isDeleteMode && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <input
                  type="checkbox"
                  className="history-checkbox"
                  checked={
                    filteredHistory.length > 0 &&
                    selectedItems.length === filteredHistory.length
                  }
                  onChange={toggleSelectAll}
                  title="Select all"
                />
              </div>
            )}
            <div className="history-grid-header-cell">ID</div>
            <div className="history-grid-header-cell">Type</div>
            <div className="history-grid-header-cell">Status</div>
            <div className="history-grid-header-cell">Started</div>
            <div className="history-grid-header-cell">Duration</div>
            <div className="history-grid-header-cell">Items</div>
            <div className="history-grid-header-cell">Actions</div>
          </div>

          {/* Virtualized list */}
          <List
            height={listHeight}
            itemCount={filteredHistory.length}
            itemSize={ROW_HEIGHT}
            width="100%"
            itemData={itemData}
          >
            {Row}
          </List>
        </>
      )}
    </div>
  );
};

export default HistoryTable;
